import { Navigate, useSearchParams } from "react-router-dom";
import { AuthPageLayout } from "./auth-page-layout";
import { useAuth } from "@/hooks/use-auth";
import { buttonVariants } from "@/components/ui/button";
import { Spinner } from "@/components/ui";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";

// The four loopback params `authorize` forwarded verbatim through this
// redirect (see backend/apps/accounts/views_desktop_auth.py
// `_loopback_params`). Threaded straight back onto the continue/switch
// links so the browser's next request carries the same port/state/PKCE
// challenge the desktop app is waiting on.
const LOOPBACK_PARAM_KEYS = [
  "port",
  "state",
  "code_challenge",
  "code_challenge_method",
] as const;

function desktopAuthorizeUrl(
  loopbackParams: URLSearchParams,
  action: "confirm" | "switch",
): string {
  const query = new URLSearchParams(loopbackParams);
  query.set(action, "1");
  return `/api/v1/auth/desktop/authorize/?${query.toString()}`;
}

export function DesktopConfirmPage() {
  const t = useT(authMessages);
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading } = useAuth();
  const port = searchParams.get("port");
  const codeChallenge = searchParams.get("code_challenge");
  const accountName = user?.name?.trim() || user?.email || t("currentAccount");

  if (!port || !codeChallenge) {
    return (
      <AuthPageLayout>
        <div className="auth-state-stack" data-testid="desktop-confirm-missing">
          <h2 className="auth-page-title">{t("linkExpired")}</h2>
        </div>
      </AuthPageLayout>
    );
  }

  const loopbackParams = new URLSearchParams();
  for (const key of LOOPBACK_PARAM_KEYS) {
    loopbackParams.set(key, searchParams.get(key) ?? "");
  }
  const continueHref = desktopAuthorizeUrl(loopbackParams, "confirm");
  const switchHref = desktopAuthorizeUrl(loopbackParams, "switch");

  if (isLoading) {
    return (
      <AuthPageLayout>
        <div
          className="flex justify-center py-10"
          data-testid="desktop-confirm-loading"
        >
          <Spinner className="h-8 w-8" />
        </div>
      </AuthPageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/auth/login?next=${encodeURIComponent(continueHref)}`}
        replace
      />
    );
  }

  return (
    <AuthPageLayout>
      <div className="auth-state-stack" data-testid="desktop-confirm">
        <h2 className="auth-page-title">{t("connectOnThisMac")}</h2>

        <div className="auth-action-stack">
          <a
            href={continueHref}
            className={buttonVariants({
              variant: "default",
              size: "lg",
              className:
                "auth-liquid-primary-button w-full min-w-0 justify-center font-semibold",
            })}
            data-testid="desktop-confirm-continue"
          >
            {t.rich("continueAs", {
              name: (
                <span
                  className="min-w-0 truncate"
                  title={accountName}
                  data-testid="desktop-confirm-account-name"
                >
                  {accountName}
                </span>
              ),
            })}
          </a>

          <a
            href={switchHref}
            className={buttonVariants({
              variant: "secondary",
              size: "lg",
              className: "auth-liquid-secondary-button w-full font-semibold",
            })}
            data-testid="desktop-confirm-switch"
          >
            {t("switchAccount")}
          </a>
        </div>
      </div>
    </AuthPageLayout>
  );
}
